import { iconIndex } from 'api/algolia';
import {
  Icons8IllustrationSearchResponse,
  Icons8SearchParams,
  Icons8SearchResponse,
  icons8IllustrationSearch,
  icons8Search,
} from 'libs/e1api/icons8';
import { GATED_ICONS8_PLATFORMS } from 'modules/Editor/components/WidgetMenu/SideMenu/IconWidgetMenu/DefaultIconView/iconPackData';
import { UserTypes } from 'modules/User';
import { IconColorOption } from 'types/icon.types';

import { parseAlgoliaIcons, parseIcons8Icons, parseIcons8Illustrations } from '../common/usePaginatedIcon.helpers';
import { IconSearchData } from '../common/usePaginatedIcon.types';
import { emptyIcons8IllustrationQuery, restrictSearchableAttributes } from './usePaginatedIconSearch.config';
import { SearchApiResponse } from './usePaginatedIconSearch.types';

const DEFAULT_HITS_PER_PAGE_SEARCH = 15;
export const ICONS8_ILLUSTRATION_HITS_PER_PAGE = 15;
const DEFAULT_HITS_PER_PAGE_PACK = 36;
const KEY_SEPARATOR = '/_';

/**
 * Generate key to be used by swr cache
 *
 * Returns key with format
 * PREFIX/_QUERY/_PAGE/_STYLE
 *
 * @param prefix
 * @param query
 * @param page
 * @param style
 * @returns
 */
export const generateKey = (prefix: string, query: string, page: number, style: string, filter?: string) => {
  return `${prefix}${KEY_SEPARATOR}${query}${KEY_SEPARATOR}${page}${KEY_SEPARATOR}${style}${
    filter ? KEY_SEPARATOR + filter : ''
  }`;
};

/**
 * Parse key into query, page, and style params
 * Key should be generated from `generateKey` function
 *
 * @param key
 * @returns
 */
export const parseKey = (key: string) => {
  const [, query, page, style, filter] = key.split(KEY_SEPARATOR);
  return {
    query,
    page,
    style,
    filter,
  };
};

/**
 * Fetch icons using Algolia search
 *
 * @param {string} key
 * @returns
 */
export const fetchAlgoliaIcons = (key: string) => {
  const { query, page, style, filter } = parseKey(key);

  const attributeStyle = style === IconColorOption.All ? 'attr_style:' : `attr_style: ${style}`;
  const facetFilters = [attributeStyle, 'published:-0'];
  const hitsPerPage = DEFAULT_HITS_PER_PAGE_SEARCH;

  if (filter !== undefined && typeof filter === 'string') {
    facetFilters.push(`iconPacks:${filter}`);
  }

  return iconIndex
    .search(query, { hitsPerPage, facetFilters, restrictSearchableAttributes, page: +page })
    .then((content) => content);
};

/**
 * Fetch icons using icons8 E1 search endpoint
 *
 * @param {string} key
 * @returns
 */
export const fetchIcons8Icons = (key: string) => {
  const { query, page, style, filter } = parseKey(key);

  const icons8SearchParams: Icons8SearchParams = {
    // Remove special characters - E1 endpoint doesn't support special characters
    query: query.replace(/[^a-zA-Z0-9 ]/g, ''),
    limit: DEFAULT_HITS_PER_PAGE_SEARCH,
    offset: +page * DEFAULT_HITS_PER_PAGE_SEARCH,
  };

  // Only specify isColor filter if a filter is applied (i.e. not All)
  if (style !== IconColorOption.All) {
    icons8SearchParams.isColor = style === IconColorOption.Color;
  }

  if (filter !== undefined && typeof filter === 'string') {
    icons8SearchParams.platform = filter;
  }

  return icons8Search(icons8SearchParams).then((content) => content);
};

export const fetchIcons8Illustrations = (key: string) => {
  const { query, page, style, filter } = parseKey(key);

  // Illustrations are only available in color
  if (style === IconColorOption.Mono) return Promise.resolve(emptyIcons8IllustrationQuery);

  const icons8SearchParams: Icons8SearchParams = {
    // Remove special characters - E1 endpoint doesn't support special characters
    query: query.replace(/[^a-zA-Z0-9 ]/g, ''),
    limit: ICONS8_ILLUSTRATION_HITS_PER_PAGE,
    offset: +page + 1,
  };

  if (filter !== undefined && typeof filter === 'string') {
    icons8SearchParams.styles = filter;
  }

  return icons8IllustrationSearch(icons8SearchParams).then((content) => content);
};

/**
 * Parses icons from each icon search response
 * For now, only from Algolia and Icons8
 * Returns normalized data to be rendered in the icon left menu
 *
 * @param algoliaData Algolia search response
 * @param icons8Data Icons8 search response
 * @param icons8IllustrationData Icons8 illustration search response
 * @param icons8IconsApiData Icons8 icons api search response
 * @returns IconSearchData[] - normalized icon data from the search responses
 */
export const parseAllIcons = (
  algoliaData: SearchApiResponse[],
  icons8Data: Icons8SearchResponse[],
  icons8IllustrationData: Icons8IllustrationSearchResponse[],
) => {
  let concatenatedIcons: IconSearchData[] = [];

  const algoliaIcons = parseAlgoliaIcons(algoliaData).map((icon) => ({
    id: icon.id,
    name: icon.name,
    permission: icon.type,
    isIllustration: icon.illustration === 1,
    isMono: icon.attr_style === 'mono',
  }));

  const icons8Icons = parseIcons8Icons(icons8Data).map(({ id, commonName, isColor, platform, sourceFormat }) => ({
    id: id,
    name: commonName,
    isMono: !isColor,

    permission: GATED_ICONS8_PLATFORMS[UserTypes.Premium].includes(platform)
      ? ('premium' as const)
      : ('default' as const),

    isIllustration: false,

    iconPath: `${platform}/${commonName}.${sourceFormat}`,
  }));

  const icons8Illustrations = parseIcons8Illustrations(icons8IllustrationData).map(({ id, urls }) => ({
    id: id,
    name: id,
    isMono: false,

    // No gating for icons8
    permission: 'default' as const,

    isIllustration: true,
    iconIllustrationThumbnailUrl: urls.thumb.url,
  }));

  // Mix Algolia and Icons8 icons together
  // Order: Algolia, Algolia, Icons8, Icons8, Illustrations, repeat
  let algoliaIdx = 0;
  let icons8Idx = 0;
  let illustrationsIdx = 0;
  while (
    algoliaIdx < algoliaIcons.length ||
    icons8Idx < icons8Icons.length ||
    illustrationsIdx < icons8Illustrations.length
  ) {
    // Add 2 algolia icons
    if (algoliaIdx < algoliaIcons.length) {
      concatenatedIcons.push(algoliaIcons[algoliaIdx]);
      algoliaIdx++;
    }
    if (algoliaIdx < algoliaIcons.length) {
      concatenatedIcons.push(algoliaIcons[algoliaIdx]);
      algoliaIdx++;
    }

    // Add 2 icons8 icons
    if (icons8Idx < icons8Icons.length) {
      concatenatedIcons.push(icons8Icons[icons8Idx]);
      icons8Idx++;
    }
    if (icons8Idx < icons8Icons.length) {
      concatenatedIcons.push(icons8Icons[icons8Idx]);
      icons8Idx++;
    }

    // Add 1 illustration icon
    if (illustrationsIdx < icons8Illustrations.length) {
      concatenatedIcons.push(icons8Illustrations[illustrationsIdx]);
      illustrationsIdx++;
    }
  }

  return concatenatedIcons;
};

/**
 * Fetches icons in a specific pack with pagination
 *
 * @param key string of form <PACK_ID>_<PAGE_NUMBER>
 * @returns
 */
export const fetchIconsByPack = (key: string) => {
  const { query: packId, page } = parseKey(key);

  const facetFilters = ['published:-0', `iconPacks:${packId}`];
  const hitsPerPage = DEFAULT_HITS_PER_PAGE_PACK;

  return iconIndex
    .search('', { hitsPerPage, facetFilters, restrictSearchableAttributes, page: +page })
    .then((content) => content);
};

/**
 * Check if the "All" icon style is included in the icon style options array
 *
 * @param iconColorOptions IconColorOption[]
 */
export const hasAllIconOption = (iconColorOptions: IconColorOption[]): boolean =>
  iconColorOptions.includes(IconColorOption.All);

/**
 * Check if there are more icons8 icons to load based on the last search response received
 *
 * @param lastIcons8Response The last Icons8 search response
 * @returns boolean
 */
export const hasMoreIcons8Icons = (lastIcons8Response: Icons8SearchResponse) => {
  if (
    !lastIcons8Response ||
    !lastIcons8Response.parameters ||
    !lastIcons8Response.parameters.countAll ||
    typeof lastIcons8Response.parameters.offset !== 'number'
  )
    return false;

  const totalNumPages = Math.ceil(lastIcons8Response.parameters.countAll / DEFAULT_HITS_PER_PAGE_SEARCH);
  const lastPage = Math.floor(lastIcons8Response.parameters.offset / DEFAULT_HITS_PER_PAGE_SEARCH);

  return lastPage < totalNumPages - 1;
};
